import React, { useEffect, useState } from 'react';
import { BrowserRouter, useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';

import { AngularServicesContext } from 'react-app';
import t from 'react-translate';

import { AnalyticsDashBoardType } from 'redux/schemas/app/analytics';
import { fetchCohorts } from 'redux/actions/analytics';
import { useAppDispatch } from 'redux/store';

import { gray6, warning } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { resetQrveyValues } from 'analytics_dashboard/hooks/use-initialize-qrvey';
import QuizAndSurveyAnalyticsTab from './quiz-survey-analytics';
import QrveyWrapper from './qrvey-wrapper';
import AnalyticsDashboard from './analytics-dashboard';
import QrveyPanelsWrapper from './qrvey-panels-wrapper';

enum CohortTabs {
  OVERVIEW = 0,
  QUIZ_SURVEY = 1,
}

const styles = css`
  .tab-container {
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: solid 1px ${gray6};
    height: 60px;
  }
  .bottom-border {
    border-bottom: solid ${quarterSpacing}px ${warning}
  }
  .quiz-survey-dashboard {
    max-width: 1030px;
  }
  .question-title {
    max-width: 800px;
  }
`;

export const getBaseUrl = (catalogId) => (`/#!/courses/${catalogId}/cohort-analytics`);

const CohortAnalytics: React.FC = () => {
  const [questionSetId, setQuestionSetId] = useState(null);
  const [activeTab, setActiveTab] = useState(CohortTabs.OVERVIEW);
  const history = useHistory();
  const dispatch = useAppDispatch();

  const institutionId = useSelector((state) => state.app.currentInstitutionId);
  const cohorts = useSelector(state => state.app.analytics.cohorts);

  const { $state } = React.useContext(AngularServicesContext);
  const { catalogId } = $state?.params;
  const BASE_URL = getBaseUrl(catalogId);

  const onSelectQuestion = (id: number) => {
    setQuestionSetId(id);
    history.push(`${BASE_URL}/quiz-and-survey/${id}`);
  };

  const onSelectTab = (tab: CohortTabs, id?: number) => {
    resetQrveyValues();
    setActiveTab(tab);
    if (id) {
      onSelectQuestion(id);
    } else {
      setQuestionSetId(null);
      window.location.href = BASE_URL;
    }
  };

  useEffect(() => {
    if (questionSetId) {
      dispatch(fetchCohorts({ catalogId, institutionId, questionSetId }));
    }
  }, [dispatch, institutionId, catalogId, questionSetId]);

  useEffect(() => {
    if ($state.params?.questionId) {
      setQuestionSetId($state.params?.questionId);
    }
  }, [$state.params?.questionId]);

  useEffect(() => {
    if (questionSetId) {
      setActiveTab(CohortTabs.QUIZ_SURVEY);
    }
  }, [questionSetId]);

  return (
    <div css={styles}>
      <div className='d-flex justify-content-between bg-white px-4 tab-container'>
        <div className='d-flex pr-2'>
          <ClickableContainer
            className={`d-flex align-items-center card-title px-2 ${activeTab === CohortTabs.OVERVIEW ? 'bottom-border' : ''}`}
            onClick={() => onSelectTab(CohortTabs.OVERVIEW)}
          >
            {t.COURSE_ANALYTICS.OVERVIEW()}
          </ClickableContainer>
          <QuizAndSurveyAnalyticsTab
            selectedId={questionSetId}
            onSelect={(id) => onSelectTab(CohortTabs.QUIZ_SURVEY, id)}
            isCohort
          />
        </div>
        <div className='d-flex align-items-center'>
          <a
            href={$state?.href('course-analytics', { catalogId })}
            className='d-flex text-primary font-weight-bold '
            aria-labelledby='course-analytics-link'
          >
            <NvIcon icon='course' size='smallest' />
            <span className='ml-1' id='course-analytics-link'>
              {t.INSTITUTIONS.COURSE_ANALYTICS.HEADER()}
            </span>
          </a>
        </div>
      </div>
      {activeTab === CohortTabs.OVERVIEW && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COHORTS}
          configKeyName='config_cohorts'
          pageIdKey='cohorts'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {activeTab === CohortTabs.QUIZ_SURVEY && (
        <div className='d-flex justify-content-center quiz-survey-dashboard mx-auto'>
          <AnalyticsDashboard
            dashboardType={AnalyticsDashBoardType.COHORT_QUESTION_SET}
            configKeyName={`config_cohorts_question_set_${questionSetId}`}
            pageIdKey='cohorts_question_set'
            questionSetId={questionSetId}
            cohorts={cohorts}
            isCohort
          >
            <QrveyPanelsWrapper />
          </AnalyticsDashboard>
        </div>
      )}
    </div>
  );
};

export default CohortAnalytics;

export const CohortAnalyticsRoute = () => <BrowserRouter forceRefresh><CohortAnalytics /></BrowserRouter>;
